import { Link, type To } from 'react-router-dom'
import {
  Activity,
  BarChart3,
  Bell,
  Bot,
  Building2,
  CheckCircle2,
  ClipboardList,
  CreditCard,
  FileBarChart,
  FileSpreadsheet,
  FileText,
  History,
  Landmark,
  LifeBuoy,
  PieChart,
  Receipt,
  Repeat,
  ScrollText,
  ShieldCheck,
  ShoppingCart,
  Upload,
  UserPlus,
  Users,
  Workflow,
  type LucideIcon,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

type GuideLink = { to: To; label: string; icon: LucideIcon }

function GuideLinkInline({ to, label, icon: Icon }: GuideLink) {
  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
    >
      <Icon className="size-4" />
      {label}
    </Link>
  )
}

function Bullet({ children }: { children: React.ReactNode }) {
  return (
    <li className="relative pl-6 leading-relaxed text-muted-foreground before:absolute before:left-1 before:top-0 before:text-lg before:font-bold before:text-success before:content-['•']">
      {children}
    </li>
  )
}

export function GuidePage() {
  return (
    <Page
      title="Application User Guide"
      description="Master your financial operations with our comprehensive guide to BoKapSys features and workflows."
      width="wide"
    >
      <Card>
        <CardHeader>
          <CardTitle>Dashboard &amp; Smart Chat</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <p>Your central command center for financial oversight.</p>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Financial Overview</h3>
            <p>
              The <GuideLinkInline to="/dashboard" label="Dashboard" icon={PieChart} /> provides a
              real-time snapshot of your financial health. View dynamic charts representing income
              versus expenses, recent transaction summaries, and quick alerts regarding your
              account status.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Smart Chat</h3>
            <p>
              Your intelligent financial assistant is available 24/7. Use the{' '}
              <GuideLinkInline to="/chatbot" label="Smart Chat" icon={Bot} /> chat interface to:
            </p>
            <ul className="space-y-2">
              <Bullet>Ask complex questions about your financial history.</Bullet>
              <Bullet>Get summaries of your spending habits.</Bullet>
              <Bullet>Receive insights on potential tax deductions or financial strategies.</Bullet>
            </ul>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Statements Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <p>
            The core of the application where your financial data is ingested, processed, and
            categorized.
          </p>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Process Statements</h3>
            <p>
              Navigate to{' '}
              <GuideLinkInline to="/upload" label="Process Statements" icon={Upload} /> to upload
              your bank or credit card statements. The system supports PDF formats and uses
              advanced parsing to extract transaction data automatically.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">View Statements</h3>
            <p>
              This is your primary ledger view. Visit{' '}
              <GuideLinkInline to="/view-statements" label="View Statements" icon={FileSpreadsheet} />{' '}
              to:
            </p>
            <ul className="space-y-2">
              <Bullet>
                <strong className="text-foreground">View Transactions:</strong> See a detailed list
                of every extracted transaction.
              </Bullet>
              <Bullet>
                <strong className="text-foreground">Filter &amp; Search:</strong> Use advanced
                filters to find specific entries by date, amount, or category.
              </Bullet>
              <Bullet>
                <strong className="text-foreground">Edit Data:</strong> Correct any categorization
                errors directly in the grid using the &apos;Edit&apos; button.
              </Bullet>
              <Bullet>
                <strong className="text-foreground">Delete Data:</strong> Use the
                &apos;Delete&apos; button on any row to remove a transaction, or use checkboxes for
                bulk deletion.
              </Bullet>
            </ul>
          </section>

          <div className="rounded-md border-l-4 border-primary bg-primary/5 p-5">
            <span className="mb-2 block text-base font-semibold text-primary">
              Allocation Schedules
            </span>
            <p>
              Under the Accounting section in the sidebar, expand{' '}
              <strong className="text-foreground">Allocation Schedules</strong> to access:
            </p>
            <ul className="mt-3 space-y-2">
              <Bullet>
                <strong className="text-foreground">Depreciation:</strong> Create fixed asset
                depreciation schedules (SLN, DB, SYD, DDB methods). Entries post as DR Depreciation
                Expense / CR Accumulated Depreciation.
              </Bullet>
              <Bullet>
                <strong className="text-foreground">Prepaid Amortization:</strong> Amortize prepaid
                expenses over their benefit period. Entries post as DR Expense / CR Prepaid Asset.
              </Bullet>
              <Bullet>
                <strong className="text-foreground">Debt Amortization:</strong> Track loan
                schedules with principal and interest breakdowns. Interest accruals post as DR
                Interest Expense / CR Accrued Interest.
              </Bullet>
            </ul>
          </div>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Analyze Statements</h3>
            <p>
              Visual analytics tools in{' '}
              <GuideLinkInline to="/analyze-statements" label="Analyze Statements" icon={BarChart3} />{' '}
              breakdown your data into spending categories, monthly trends, and vendor analysis to
              help you understand where your money goes.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Data History</h3>
            <p>
              Check <GuideLinkInline to="/data-history" label="Data History" icon={History} /> for a
              comprehensive log of all file uploads to verify when files were processed and by
              whom.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Accounting Operations</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <p>Tools designed for professional accounting standards and accuracy.</p>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Manage Accruals</h3>
            <p>
              Create and monitor accrual schedules in{' '}
              <GuideLinkInline to="/accrual-schedule" label="Manage Accruals" icon={Repeat} />.
              Recognize revenue and expenses in the period they occur, adhering to accrual-based
              accounting principles.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Financial Reports</h3>
            <p>
              Generate essential financial statements in{' '}
              <GuideLinkInline to="/reporting" label="Financial Reports" icon={FileBarChart} />:
            </p>
            <ul className="space-y-2">
              <Bullet>
                <strong className="text-foreground">Profit &amp; Loss (P&amp;L):</strong> View your
                net income over selected periods.
              </Bullet>
              <Bullet>
                <strong className="text-foreground">Balance Sheet:</strong> See a snapshot of your
                assets, liabilities, and equity.
              </Bullet>
            </ul>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Chart of Accounts (COA)</h3>
            <p>
              The backbone of your accounting system. Visit{' '}
              <GuideLinkInline to="/coa" label="Chart of Accounts" icon={ClipboardList} /> to view,
              add, or edit the account categories used to classify your transactions.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Reconciliation</h3>
            <p>
              Ensure your books match your bank records. The{' '}
              <GuideLinkInline to="/reconcile" label="Reconciliation" icon={Activity} /> tool
              highlights discrepancies between uploaded statements and ledger balances.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Invoicing &amp; AR</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Customers &amp; Invoices</h3>
            <p>
              Track receivables in{' '}
              <GuideLinkInline to="/invoicing/customers" label="Customers" icon={Users} /> and{' '}
              <GuideLinkInline to="/invoicing/invoices" label="Invoices" icon={FileText} />. Draft,
              send, post, void, or mark invoices paid. Each posted invoice writes directly to the
              ledger so the AR aging, P&amp;L, and Balance Sheet reflect changes immediately.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Credit Notes, Refunds &amp; Write-offs</h3>
            <p>
              Issue credit notes against open invoices, refund paid invoices, or write off
              uncollectible balances. Every action posts a reversing entry that keeps the AR
              balance, the customer ledger, and the financial reports in sync.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Recurring Invoices</h3>
            <p>
              Configure repeating templates from the Invoices page. The system auto-generates
              the next invoice on the cadence you set (weekly, monthly, custom) and either drafts
              it for review or sends it automatically.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Customer Portal &amp; Pay Links</h3>
            <p>
              Each customer can access a hosted, read-only portal showing their invoices and
              statements. Outgoing invoice emails carry a secure pay link that lets the customer
              settle by card without signing in.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bills &amp; AP</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Vendors &amp; Bills</h3>
            <p>
              Manage payables in{' '}
              <GuideLinkInline to="/invoicing/vendors" label="Vendors" icon={ShoppingCart} /> and{' '}
              <GuideLinkInline to="/invoicing/bills" label="Bills" icon={Receipt} />. Capture bills
              by hand or upload PDFs and images - the AI extraction layer reads vendor, line items,
              tax, and due date and pre-fills the editor for review.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Approve, Post &amp; Pay</h3>
            <p>
              Submitted bills route through the approval policy engine. Once approved, posting
              writes the AP entry; paying records the cash disbursement. Duplicate detection
              prompts before allowing two bills with the same vendor + amount + reference within
              a short window.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Approvals</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Policies, Inbox &amp; Delegations</h3>
            <p>
              Open <GuideLinkInline to="/approvals" label="Approvals" icon={CheckCircle2} /> to
              configure approval policies (thresholds, approver roles, escalation paths), see
              what is waiting on you in the inbox, and delegate authority while you are out of
              office. All money-touching mutations route through the engine even when no policy
              applies, so adding a policy later does not require code changes.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Workflows &amp; AI Agents</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Workflows</h3>
            <p>
              Build automations in{' '}
              <GuideLinkInline to="/workflows" label="Workflows" icon={Workflow} />: scheduled or
              event-driven sequences that read your data, post journal entries, send emails, or
              hand off to an AI agent. Steps run inside the shared executor so behavior is
              consistent and auditable.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">AI Agents</h3>
            <p>
              Configure assistants in{' '}
              <GuideLinkInline to="/agents" label="AI Agents" icon={Bot} /> to monitor data, draft
              responses, or run on a cron. Each agent has a visibility setting (private, tenant,
              or shared with named members) so personal experiments stay separate from
              tenant-shared automations.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Audit, Notifications &amp; Team</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Audit Log</h3>
            <p>
              Every mutation - invoice posted, bill paid, refund issued, role changed,
              permission granted - is recorded in the append-only{' '}
              <GuideLinkInline to="/team-management" label="Audit Log" icon={ScrollText} />. Filter
              by actor, action, or time range; export to CSV for compliance reviews.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Notifications</h3>
            <p>
              The bell in the header surfaces tenant-scoped events: payments received, refunds
              processed, payouts run, approvals waiting, workflow outcomes. Open{' '}
              <GuideLinkInline to="/notifications" label="Notifications" icon={Bell} /> for the
              full feed.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Team &amp; Roles</h3>
            <p>
              Invite teammates, create custom roles, and assign permissions in{' '}
              <GuideLinkInline to="/team-management" label="Team Management" icon={ShieldCheck} />.
              Permissions are tenant-scoped - your team only sees what their role allows within
              your workspace.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Banking &amp; Client Management</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Bank Feeds (Plaid)</h3>
            <p>
              Connect your bank accounts securely in{' '}
              <GuideLinkInline to="/plaid" label="Bank Feeds" icon={Landmark} />. This establishes
              a live link to your financial institutions for automatic daily imports.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Business Info</h3>
            <p>
              Use <GuideLinkInline to="/company" label="Company → Business Info" icon={UserPlus} /> to
              capture business details, key contacts, and service preferences for this workspace.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payments &amp; Payouts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Choose how you collect payments</h3>
            <p>
              Open <GuideLinkInline to="/company" label="Company" icon={Building2} /> →
              Payment Collection. Two options:
            </p>
            <ul className="space-y-1">
              <Bullet>
                <strong>Stripe Connect</strong>: customers pay you directly into your own Stripe
                account. Money lands in your bank automatically. Stripe processing fee applies; no
                Bokapsys fee.
              </Bullet>
              <Bullet>
                <strong>Bokapsys-managed</strong>: Bokapsys collects on your behalf and pays you
                weekly to the bank account you register on the same page. Stripe processing fee +
                a 1% Bokapsys platform fee come out of each payout.
              </Bullet>
            </ul>
            <p>
              Switching between modes later is one click - your existing Stripe account stays
              linked, no re-onboarding needed.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Payments dashboard</h3>
            <p>
              <GuideLinkInline to="/invoicing/payments" label="Payments" icon={CreditCard} /> shows
              your live balance, recent invoice payments with the full fee breakdown
              (Gross / Stripe fee / Bokapsys fee / Net), and your payout history. Connect-mode
              tenants can also expand the embedded Stripe panels for deep account management
              inline.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Refunds</h3>
            <p>
              Each Stripe payment row has a <strong>Refund</strong> button (gated by the
              <em> Refunds - Issue</em> permission). Pick a partial or full amount, choose a reason,
              and the refund goes back to the card. The Refunds card on the dashboard tracks status
              (pending / succeeded / failed) live from Stripe, and the Pending Net subtracts
              refunds in red.
            </p>
            <p>
              Stripe&apos;s processing fee is <strong>not</strong> returned on a refund - whoever
              paid it originally absorbs that loss.
            </p>
          </section>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Account Settings</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5 text-sm leading-relaxed text-muted-foreground">
          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Profile</h3>
            <p>
              Manage your personal details and avatar in{' '}
              <GuideLinkInline to="/profile" label="Profile" icon={Users} />.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Subscriptions</h3>
            <p>
              View your current plan and usage limits in{' '}
              <GuideLinkInline to="/subscriptions" label="Subscriptions" icon={CreditCard} />.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Contact us</h3>
            <p>
              Need help, want to report a bug, or have a feature request? Visit{' '}
              <GuideLinkInline to="/contact-sales" label="Contact us" icon={LifeBuoy} /> and pick
              the option that fits.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Company</h3>
            <p>
              Configure your workspace details and fiscal calendar in{' '}
              <GuideLinkInline to="/company" label="Company" icon={Building2} />.
            </p>
          </section>

          <section className="space-y-2">
            <h3 className="text-base font-semibold text-primary">Expenses</h3>
            <p>
              Track fixed asset depreciation schedules in{' '}
              <GuideLinkInline to="/fixed-assets" label="Fixed Assets" icon={Receipt} />.
            </p>
          </section>
        </CardContent>
      </Card>
    </Page>
  )
}
